import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { showcaseSettingsPatch } from "./showcaseSettingsPatch";

const defaultSettings = {
  promoShowcaseEnabled: false,
  promoShowcaseUrl: "",
  promoShowcaseHosting: null,
  promoShowcaseApiKey: "",
  promoShowcaseApiSecret: "",
  promoShowcaseConnected: false,
  promoShowcaseConnectedAt: null,
  promoShowcaseLastError: null,
  promoShowcaseBlogConnectorPath: '/include/elitecore-blog.php',
  requestDesignerLabel: 'Request designer',
  designerTeamUserIds: [],
};

export default function PromoShowcaseSettingsPanel({ instanceUniqueId }) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [settings, setSettings] = useState(defaultSettings);
  const savedSettings = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [hasBeenEnabled, setHasBeenEnabled] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);

  const connectedStatus = settings.promoShowcaseConnected ? "Connected" : "Not connected";
  const connectedColor = settings.promoShowcaseConnected ? "success" : "warning";

  const canEdit = settings.promoShowcaseEnabled && !loading && !saving;

  const loadSettings = async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/settings`,
        headers
      );
      const next = { ...defaultSettings, ...(res.data?.settings || {}) };
      const loaded = {
        ...next,
        promoShowcaseUrl: next.promoShowcaseUrl || "",
        promoShowcaseHosting: next.promoShowcaseHosting && typeof next.promoShowcaseHosting === "object" ? next.promoShowcaseHosting : null,
        promoShowcaseApiKey: next.promoShowcaseApiKey || "",
        promoShowcaseApiSecret: next.promoShowcaseApiSecret || "",
        promoShowcaseBlogConnectorPath: next.promoShowcaseBlogConnectorPath || '/include/elitecore-blog.php',
        requestDesignerLabel: next.requestDesignerLabel || 'Request designer',
        designerTeamUserIds: Array.isArray(next.designerTeamUserIds) ? next.designerTeamUserIds : [],
      };
      savedSettings.current = { instanceUniqueId, settings: loaded };
      setSettings(loaded);
      setTeamUsers(res.data?.teamUsers || []);
      setHasBeenEnabled(!!(next.promoShowcaseEnabled || next.promoShowcaseConnected));
    } catch (error) {
      toast.error("Failed to load PromoShowcase settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    const fetchEligibleUsers = async () => {
      if (!instanceUniqueId) return;
      try {
        const instanceRes = await axios.get(
          `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          headers
        );
        const instanceId = instanceRes.data?.id;
        if (!instanceId) return;

        const roles = ["administrator", "promoshowcase-manager", "promostores-manager"];
        const requests = roles.map((role) =>
          axios.get(`${SERVER}/auth/users/picker`, {
            params: { instanceId, role, pageSize: 200 },
            headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
          })
        );

        const responses = await Promise.all(requests);
        const combined = responses.flatMap((res) => res.data?.users || []);
        const unique = new Map();
        combined.forEach((user) => {
          if (user?.id != null) unique.set(user.id, user);
        });
        setEligibleUsers(Array.from(unique.values()));
      } catch (error) {
        console.error("Failed to load PromoShowcase designer candidates:", error);
      }
    };

    fetchEligibleUsers();
  }, [SERVER, instanceUniqueId]);

  const updateSettings = async (override = {}) => {
    setSaving(true);
    try {
      if (savedSettings.current?.instanceUniqueId !== instanceUniqueId) throw new Error('Load settings before saving changes.');
      const payload = showcaseSettingsPatch(savedSettings.current.settings, settings, override);
      const unsaved = Object.keys(override).length ? showcaseSettingsPatch(savedSettings.current.settings, settings) : {};
      Object.keys(payload).forEach(key => { delete unsaved[key]; });
      const res = await axios.put(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/settings`,
        payload,
        headers
      );
      const next = { ...defaultSettings, ...(res.data?.settings || payload) };
      const loaded = {
        ...next,
        promoShowcaseUrl: next.promoShowcaseUrl || "",
        promoShowcaseHosting: next.promoShowcaseHosting && typeof next.promoShowcaseHosting === "object" ? next.promoShowcaseHosting : null,
        promoShowcaseApiKey: next.promoShowcaseApiKey || "",
        promoShowcaseApiSecret: next.promoShowcaseApiSecret || "",
        promoShowcaseBlogConnectorPath: next.promoShowcaseBlogConnectorPath || '/include/elitecore-blog.php',
        requestDesignerLabel: next.requestDesignerLabel || 'Request designer',
        designerTeamUserIds: Array.isArray(next.designerTeamUserIds) ? next.designerTeamUserIds : [],
      };
      savedSettings.current = { instanceUniqueId, settings: loaded };
      setSettings({ ...loaded, ...unsaved, ...(unsaved.promoShowcaseHosting
        ? { promoShowcaseHosting: { ...loaded.promoShowcaseHosting, ...unsaved.promoShowcaseHosting } } : {}) });
      setTeamUsers(res.data?.teamUsers || []);
      if (payload.promoShowcaseEnabled) {
        setHasBeenEnabled(true);
      }
      toast.success("PromoShowcase settings saved.");
      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to save PromoShowcase settings."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (_event, checked) => {
    if (!checked && hasBeenEnabled) {
      setConfirmOpen(true);
      return;
    }
    setSettings((prev) => ({ ...prev, promoShowcaseEnabled: checked }));
    updateSettings({ promoShowcaseEnabled: checked });
  };

  const handleConfirmDisconnect = async () => {
    setConfirmOpen(false);
    setSettings((prev) => ({ ...prev, promoShowcaseEnabled: false }));
    await updateSettings({ promoShowcaseEnabled: false });
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      // 1) Save what’s currently typed (so DB has it)
      if (!await updateSettings()) return;
  
      // 2) Now connect (backend reads from DB)
      await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/connect`,
        {},
        headers
      );
  
      toast.success("PromoShowcase connected");
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Unable to connect to PromoShowcase. Verify URL and ensure WordPress ASI Client ID/Secret exactly match these fields.";
      setSettings((prev) => ({ ...prev, promoShowcaseLastError: message }));
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };
  

  const connectLabel = settings.promoShowcaseConnected ? "Reconnect" : "Connect";

  const saveDisabled = !settings.promoShowcaseEnabled || saving || loading;

  const selectedUsers = useMemo(() => {
    if (!settings.designerTeamUserIds.length) return [];
    const eligibleMap = new Map(eligibleUsers.map((user) => [user.id, user]));
    const teamMap = new Map(teamUsers.map((user) => [user.id, user]));
    return settings.designerTeamUserIds
      .map((id) => eligibleMap.get(id) || teamMap.get(id))
      .filter(Boolean);
  }, [eligibleUsers, settings.designerTeamUserIds, teamUsers]);

  const statusLabel = useMemo(() => {
    if (!settings.promoShowcaseEnabled) return "Disabled";
    return connectedStatus;
  }, [connectedStatus, settings.promoShowcaseEnabled]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">PromoShowcase</Typography>
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <Chip
                size="small"
                color={settings.promoShowcaseEnabled ? connectedColor : "default"}
                label={statusLabel}
              />
            )}
          </Box>
        }
        action={
          <Button
            variant="contained"
            onClick={() => updateSettings()}
            disabled={saveDisabled}
          >
            {saving ? "Saving..." : "Save PromoShowcase Settings"}
          </Button>
        }
        subheader="Connect PromoShowcase to sync data with your WordPress site."
      />
      <Divider />
      <CardContent>
        <Box sx={{ display: "grid", gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.promoShowcaseEnabled}
                onChange={handleToggle}
                disabled={loading || saving}
              />
            }
            label="Enable PromoShowcase"
          />

          <TextField
            label="WordPress Connection URL"
            value={settings.promoShowcaseUrl}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                promoShowcaseUrl: e.target.value,
                promoShowcaseHosting: {
                  ...(prev.promoShowcaseHosting || {}),
                  networkBaseUrl: (prev.promoShowcaseHosting || {}).networkBaseUrl || "https://showcase.elitecore.ca",
                },
              }))
            }
            fullWidth
            disabled={!canEdit}
            helperText="Stable technical URL used by Elite Core, e.g. https://showcase.elitecore.ca/acme/"
          />
          <TextField
            label="Public / Custom Domain (optional)"
            value={settings.promoShowcaseHosting?.publicUrl || ""}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                promoShowcaseHosting: {
                  ...(prev.promoShowcaseHosting || {}),
                  publicUrl: e.target.value,
                },
              }))
            }
            fullWidth
            disabled={!canEdit}
            helperText="Customer-facing domain only. Example: https://shop.acme.com. Leave blank to use the Showcase network URL."
          />
          <TextField
            label="DNS Target / Server IP (optional)"
            value={settings.promoShowcaseHosting?.dnsTarget || ""}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                promoShowcaseHosting: {
                  ...(prev.promoShowcaseHosting || {}),
                  dnsTarget: e.target.value,
                },
              }))
            }
            fullWidth
            disabled={!canEdit}
            helperText="Reference only for manual DNS setup, e.g. the DigitalOcean Droplet or load balancer IP that the custom domain should point to."
          />
          {settings.promoShowcaseHosting ? (
            <Box sx={{ display: "grid", gap: .5, p: 1.25, border: "1px solid rgba(15,23,42,.10)", borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Network: {settings.promoShowcaseHosting.runtimeNetworkUrl || settings.promoShowcaseHosting.networkBaseUrl || "https://showcase.elitecore.ca"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Site path: {settings.promoShowcaseHosting.runtimeSitePath || settings.promoShowcaseHosting.sitePath || "/"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Public URL: {settings.promoShowcaseHosting.publicUrl || settings.promoShowcaseHosting.effectivePublicUrl || "Uses network subdirectory URL"}
              </Typography>
              {settings.promoShowcaseHosting.mappedDomainDetected ? (
                <Typography variant="caption" color="success.main">
                  WordPress reports mapped domain: {settings.promoShowcaseHosting.mappedDomain}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          <TextField
            label="API Key (matches WordPress ASI Client ID)"
            value={settings.promoShowcaseApiKey}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, promoShowcaseApiKey: e.target.value }))
            }
            fullWidth
            disabled={!canEdit}
          />
          <TextField
            label="API Secret (matches WordPress ASI Client Secret)"
            type={showSecret ? "text" : "password"}
            value={settings.promoShowcaseApiSecret}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, promoShowcaseApiSecret: e.target.value }))
            }
            fullWidth
            disabled={!canEdit}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle api secret visibility"
                    onClick={() => setShowSecret((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Blog Connector Path"
            value={settings.promoShowcaseBlogConnectorPath}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, promoShowcaseBlogConnectorPath: e.target.value }))
            }
            fullWidth
            disabled={!canEdit}
            helperText="Used for blog automation endpoint on WordPress include file. Default: /include/elitecore-blog.php"
          />

          <TextField
            label="Request designer button label"
            value={settings.requestDesignerLabel}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, requestDesignerLabel: e.target.value }))
            }
            fullWidth
            disabled={!canEdit}
            helperText="Displayed on the PromoShowcase Request Designer button."
          />

          <Autocomplete
            multiple
            options={eligibleUsers}
            value={selectedUsers}
            getOptionLabel={(option) =>
              `${option?.first_name || ""} ${option?.last_name || ""}`.trim() || option?.email || `User #${option?.id}`
            }
            onChange={(_event, value) =>
              setSettings((prev) => ({
                ...prev,
                designerTeamUserIds: value.map((user) => user.id),
              }))
            }
            disabled={!canEdit}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Designer Team"
                placeholder={canEdit ? "Search users" : "Enable PromoShowcase first"}
                helperText="These teammates receive internal Request Designer emails."
              />
            )}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={handleConnect}
              disabled={!settings.promoShowcaseEnabled || connecting || saving || loading}
            >
              {connecting ? "Connecting..." : connectLabel}
            </Button>
            {settings.promoShowcaseEnabled && (
              <Chip size="small" color={connectedColor} label={connectedStatus} />
            )}
          </Box>

          {settings.promoShowcaseEnabled && settings.promoShowcaseLastError && (
            <Typography variant="body2" color="error">
              {settings.promoShowcaseLastError}
            </Typography>
          )}
        </Box>
      </CardContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Disconnect PromoShowcase?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to disconnect PromoShowcase?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDisconnect}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
